import partition from 'lodash/partition';

import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  Action,
  canPerformPropertyAggregation,
  EntryPart,
  EntrySectionUpdateEvent,
  isGroupedEntry,
  isPropertyEntry,
  QueryFilterEntry,
  QuerySuperEntry,
} from 'common/widgets/query-entry-section/types';

import {BaseEntryRow} from '../base';
import type {EntryRow} from '../base/types';
import {SuperEntryRowEl} from './template';

/**
 * QuerySuperEntryI is a catch-all row renderer for every selector type
 *
 * TODO(pablo): break up all functionality from QueryEntrySection into their respective
 * selector type classes (SegmentEntry, CohortEntry, etc..)
 */
export class SuperEntryRow extends BaseEntryRow<QuerySuperEntry> implements EntryRow<QuerySuperEntry> {
  template = SuperEntryRowEl;

  isPlaceholder() {
    const hasEventValue = this.hasEventValue(this.entry);

    if (canPerformPropertyAggregation(this.entry.type)) {
      const hasPropertyValue = this.hasPropertyValue();
      return !hasEventValue || !hasPropertyValue;
    }

    return !hasEventValue;
  }

  hasPropertyValue() {
    const {property} = this.entry;
    return Boolean(property && (property.value || property.customPropertyId));
  }

  hasEventValue(entry: QuerySuperEntry = this.entry) {
    // When adding a new row for filter/segment, the entry will be initialized to null. For other entry types,
    // a placeholder entry is used with `value` field null.
    if (isPropertyEntry(entry)) {
      return !!this.entry.event;
    } else if (isGroupedEntry(entry)) {
      // Grouped entries are managed by a sub- query-entry-section and always have an event.
      return true;
    } else {
      return !!entry.event?.value;
    }
  }

  /**
   * Handles filter updates from a nested QueryEntrySection
   */
  handleFilterSectionUpdate(ev: EntrySectionUpdateEvent, source: EntryPart): void {
    const [behavioralFilters, filters] = partition(this.entry.filters, isFirstTimeEventFilter);
    const entries = ev.detail.entries as QueryFilterEntry[];
    const updatedEntry = ev.detail.updatedEntry as QueryFilterEntry;
    const queryReason = ev.detail.queryReason;
    // update the entry's filters operator to match the selected operator from the incoming
    // updated entry, if it's not available fetch it from the first commited filter
    const filtersOperator = updatedEntry?.filtersOperator ?? entries[0]?.filtersOperator;
    const updatedFilters = {
      [EntryPart.Filters]: filters,
      [EntryPart.BehavioralFilters]: behavioralFilters,
      [source]: entries.map((entry) => entry.event),
    };

    const entry = {
      ...this.entry,
      filters: [...updatedFilters[EntryPart.Filters], ...updatedFilters[EntryPart.BehavioralFilters]],
      filtersOperator,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index, queryReason});
  }
}
